/**
 * @fileoverview Shared helpers for resolving the 5-digit county FIPS code from
 *   jurisdiction lookup selections. Extracted from the onboarding wizard's
 *   DepartmentInfoStep so the department details slide-in and the wizard share
 *   one authoritative implementation.
 * @module system-web/utils/jurisdictionFips
 *
 * Responsibilities:
 * - Derive `jurisdiction.fipsCode` from a selected county lookup value.
 * - Provide the LookupRef shape used by jurisdiction state/county/country fields.
 *
 * Dependencies:
 * - None (pure helpers).
 *
 * Architecture:
 * - Pure functions; no I/O, no side effects.
 *
 * Notes:
 * - The county lookup `code` and `config.fipsCode` both carry the full 5-digit
 *   state+county FIPS (e.g. Orange County CA = "06059"). This is the ONLY
 *   reliable join key to geoCacheFiles (whose `_id` / `fipsCode` are the same
 *   5-digit code) — state/county name strings are inconsistent across
 *   environments, so parcel-data auto-match must never rely on them.
 */

/**
 * @contract LookupRef
 * Purpose: Reference to a lookup value stored on a tenant's jurisdiction.
 * Fields:  lookupId (optional), lookupType, code, name.
 * Rules:   `code` is the lookup's stable code (e.g. state postal code or
 *          5-digit county FIPS); `name` is the display name.
 * Sync:    local-first; persisted on the tenant document in Dexie.
 */
export interface LookupRef {
  lookupId?: string;
  lookupType: string;
  code: string;
  name: string;
}

/**
 * @process resolveCountyFips
 * Purpose: Derive the 5-digit state+county FIPS code for the selected
 *          jurisdiction county. This is the authoritative join key between
 *          the tenant jurisdiction and the `geoCacheFiles` catalog (whose
 *          `_id` / `fipsCode` are the same 5-digit code), so parcel-data
 *          auto-match must never rely on state or county name strings.
 * Inputs:  ref      — the selected county LookupRef (may be undefined when cleared).
 *          item     — the raw county lookup document, when available (carries
 *                     `config.fipsCode`, which is preferred over `code`).
 *          existing — the jurisdiction's current fipsCode, retained when a
 *                     non-county field changed and the county is untouched.
 *          cleared  — true when the county was cleared by a state/country change.
 * Outputs: The 5-digit FIPS string, or undefined when unresolvable/cleared.
 * Failure Handling: Returns undefined rather than a partial code when neither
 *          the lookup config nor the code is a valid 5-digit FIPS.
 * Idempotency: REQUIRED — pure function of its inputs.
 */
export function resolveCountyFips(
  ref: LookupRef | undefined,
  item: Record<string, any> | undefined,
  existing: string | undefined,
  cleared: boolean,
): string | undefined {
  if (cleared) return undefined;
  const fromConfig = item?.config?.fipsCode;
  if (typeof fromConfig === 'string' && /^\d{5}$/.test(fromConfig)) return fromConfig;
  if (typeof ref?.code === 'string' && /^\d{5}$/.test(ref.code)) return ref.code;
  // No county selected yet (or a non-FIPS code) — keep whatever we already had
  // so changing an unrelated field does not discard a resolved FIPS.
  return ref ? existing : undefined;
}

/**
 * @process toLookupRef
 * Purpose: Convert a raw lookup value document into the LookupRef shape stored
 *          on a tenant's jurisdiction. Centralizes the mapping so the wizard,
 *          the department details slide-in, and any future surface produce
 *          identical refs.
 * Inputs:  item — a lookup value row (from listLookups / listLookupValues).
 * Outputs: A LookupRef, or undefined when the item is falsy.
 * Failure Handling: Returns undefined for falsy input.
 * Idempotency: REQUIRED — pure.
 */
export function toLookupRef(item: Record<string, any> | undefined | null): LookupRef | undefined {
  if (!item) return undefined;
  return {
    lookupId: typeof item._id === 'string' ? item._id : undefined,
    lookupType: String(item.lookupType ?? ''),
    code: String(item.code ?? ''),
    name: String(item.name ?? item.code ?? ''),
  };
}

/**
 * @process applyJurisdictionToAddress
 * Purpose: Keep `address.country`, `address.state` and `address.county` in sync with the
 *          authoritative `jurisdiction` object after a jurisdiction save.
 *          The Address section derives its State/County from jurisdiction so
 *          there is one source of truth and the geo-cache parcel matcher has
 *          the county name without a duplicate editable field.
 * Inputs:  tenant    — the tenant document after the jurisdiction field was set.
 *          jurisdiction — the newly saved jurisdiction object.
 * Outputs: A new tenant object with `address.country`, `address.state` and
 *          `address.county` derived from `jurisdiction.country` (name, or the
 *          legacy string), `jurisdiction.state?.name` / `jurisdiction.county?.name`.
 *          Other address fields are preserved. When jurisdiction is falsy, the
 *          tenant is returned unchanged.
 * Failure Handling: Missing state/county names become undefined (cleared).
 * Idempotency: REQUIRED — pure; calling twice yields the same result.
 */
export function applyJurisdictionToAddress<T extends Record<string, any>>(
  tenant: T,
  jurisdiction: Record<string, any> | undefined,
): T {
  if (!jurisdiction || typeof jurisdiction !== 'object') return tenant;
  const country = jurisdiction.country;
  const countryName = typeof country === 'string' ? country : country?.name ?? country?.code;
  const stateName = jurisdiction.state?.name;
  const countyName = jurisdiction.county?.name;
  return {
    ...tenant,
    address: { ...(tenant.address ?? {}), country: countryName, state: stateName, county: countyName },
  };
}
